// Internal mobile helpers for DataTable: the default stacked card, the
// sortable-column dropdown shown above the list, and a compact pagination
// footer. Kept in a separate file so DataTable.tsx stays focused on the
// desktop/mobile branch logic and shared state.
import { useMemo, type ReactNode } from 'react';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';
import { Select } from './Select.js';
import type { Column, Sort } from './DataTable.js';

interface MobileSortControlProps<R> {
  columns: Column<R>[];
  sort: Sort | null;
  onChange: (next: Sort | null) => void;
  labelSortBy: string;
  labelAsc: string;
  labelDesc: string;
}

export function MobileSortControl<R>({
  columns,
  sort,
  onChange,
  labelSortBy,
  labelAsc,
  labelDesc,
}: MobileSortControlProps<R>) {
  const options = columns.map((c) => ({ value: c.key, label: c.label }));
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: 'var(--fg-2)',
      }}
    >
      <span style={{ flexShrink: 0 }}>{labelSortBy}</span>
      <Select
        value={sort?.key ?? ''}
        onChange={(key) => {
          if (!key) {
            onChange(null);
            return;
          }
          onChange({ key, dir: sort?.dir ?? 'asc' });
        }}
        options={[{ value: '', label: '—' }, ...options]}
        style={{ flex: 1, minWidth: 0 }}
      />
      <IconButton
        icon="chevron-down"
        label={sort?.dir === 'asc' ? labelAsc : labelDesc}
        // WHY: button visible regardless of selected sort column. When no sort
        // is active, toggling flips direction onto the first sortable column.
        onClick={() => {
          if (!sort) {
            const first = columns[0];
            if (first) onChange({ key: first.key, dir: 'asc' });
            return;
          }
          onChange({ key: sort.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
        }}
        style={{
          transform: sort?.dir === 'asc' ? 'rotate(180deg)' : 'none',
          transition: 'transform var(--dur-1) var(--ease-out)',
        }}
      />
    </div>
  );
}

interface PaginationProps {
  page: number;
  pages: number;
  total: number;
  setPage: (next: number | ((p: number) => number)) => void;
  compact: boolean;
  labelPageOfTotal: string;
  labelBack: string;
  labelNext: string;
}

export function Pagination({
  page,
  pages,
  setPage,
  compact,
  labelPageOfTotal,
  labelBack,
  labelNext,
}: PaginationProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: compact ? 'wrap' : 'nowrap',
        gap: 8,
        padding: '8px 4px',
        fontSize: 12,
        color: 'var(--fg-2)',
      }}
    >
      <div>{labelPageOfTotal}</div>
      <div style={{ display: 'flex', gap: 4 }}>
        <Button
          size="sm"
          kind="ghost"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          icon="chevron-left"
        >
          {labelBack}
        </Button>
        <Button
          size="sm"
          kind="ghost"
          disabled={page >= pages - 1}
          onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
          trailingIcon="chevron-right"
        >
          {labelNext}
        </Button>
      </div>
    </div>
  );
}

interface DefaultMobileCardProps<R> {
  row: R;
  columns: Column<R>[];
  onSelect: ((row: R) => void) | undefined;
}

export function DefaultMobileCard<R>({ row, columns, onSelect }: DefaultMobileCardProps<R>) {
  const labelled = columns.filter((c) => c.label.length > 0);
  const actions = columns.filter((c) => c.label.length === 0);
  const clickable = !!onSelect;
  const accessibleName = useMemo(() => {
    const first = labelled[0];
    if (!first) return '';
    const value = first.render ? null : (row[first.key as keyof R] as unknown);
    return typeof value === 'string' || typeof value === 'number' ? String(value) : first.label;
  }, [labelled, row]);

  return (
    // WHY: implicit role=article so tests can target cards semantically.
    // Action wrapper below stops click + keydown propagation so action
    // controls don't bubble into the article onSelect handler. Block
    // disable covers article+onClick and article+tabIndex; Enter/Space
    // activation is wired in the keyboard handler below.
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    <article
      aria-label={accessibleName || undefined}
      tabIndex={clickable ? 0 : -1}
      onClick={() => onSelect?.(row)}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(row);
              }
            }
          : undefined
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
        cursor: clickable ? 'pointer' : 'default',
        outline: 'none',
        minWidth: 0,
        // WHY: clip pathologically long unbreakable tokens so a single
        // labelled value can never push the page scroll width past the
        // viewport at 320px.
        overflow: 'hidden',
      }}
      onFocus={(e) => {
        if (clickable) e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-ring)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {actions.length > 0 && (
        // WHY: action wrapper stops click + keydown propagation so a kebab
        // press (mouse, Enter, or Space) never bubbles up to the article
        // onSelect handler. Callers' interactive children keep their own
        // roles + keyboard handlers; this wrapper is a static guard.
        /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 4,
            marginBottom: -4,
            flexWrap: 'wrap',
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {actions.map((c) => (
            <span key={c.key}>{c.render ? c.render(row) : null}</span>
          ))}
        </div>
      )}
      {labelled.map((c) => (
        <div
          key={c.key}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
            fontSize: 13,
          }}
        >
          <span style={{ color: 'var(--fg-3)', fontSize: 11, flexShrink: 0 }}>{c.label}</span>
          <span
            style={{
              color: 'var(--fg-1)',
              textAlign: 'right',
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            {c.render ? c.render(row) : (row[c.key as keyof R] as ReactNode)}
          </span>
        </div>
      ))}
    </article>
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  );
}
